import { useRef, useState, useEffect, useCallback } from 'react';
import {
  Volume2, VolumeX, Bell, ShieldAlert, AlertTriangle,
  CheckCircle2, FileText, Settings2, Info, MessageSquare,
  Plus, Wrench, Trash2, Shield, Tag, Calendar,
  Upload, Play, X, ChevronDown, ChevronUp, Pencil,
  Radio, RefreshCw, CheckCircle, AlertCircle, Clock,
  Mail, Server, Send, MapPin,
  Flame, Heart, Phone, RotateCcw,
  ChevronLeft, ChevronRight, Mic,
  HelpCircle, Navigation2,
} from 'lucide-react';
import type { AlertType } from '@asi/shared';
import { supabase } from '@/services/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useSettingsStore,
  type SoundSlot,
  type BuiltinSoundId,
  BUILTIN_SOUND_LABELS,
  type QuickReply,
  type QuickReplyModal,
  type QuickReplyColor,
  type NtpSettings,
  CHIP_COLORS,
  CHIP_SWATCH_CLS,
  chipCls,
} from '@/stores/settings-store';
import { playBuiltinSound, playCustomAudio } from '@/utils/sound-player';
import { syncNtpOnce, getNtpStatus } from '@/utils/ntp-sync';
import { cn } from '@/lib/utils';

// ─── Changelog data (shared) ─────────────

import { CHANGELOG_DATA, type ChangelogEntry } from '@/data/changelog';

const SECTION_CONFIG = {
  added:      { label: 'Agregado',   icon: Plus,          color: 'bg-green-500/10 text-green-500 border-green-500/20' },
  changed:    { label: 'Cambiado',   icon: Wrench,        color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  deprecated: { label: 'Deprecado',  icon: AlertTriangle, color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
  removed:    { label: 'Eliminado',  icon: Trash2,        color: 'bg-red-500/10 text-red-500 border-red-500/20' },
  fixed:      { label: 'Corregido',  icon: Wrench,        color: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
  security:   { label: 'Seguridad',  icon: Shield,        color: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
};

// ─── Small helpers ────────────────────────────────────────────

// ─── Shared mini-components ────────────────────────────────

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        checked ? 'bg-amber-500' : 'bg-muted-foreground/30'
      )}
    >
      <span className={cn('inline-block h-4 w-4 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-6' : 'translate-x-1')} />
    </button>
  );
}

// ─── Per-alert-type slot config ────────────────────────────

const ALERT_TYPE_CONFIG: Record<AlertType, { icon: React.ElementType; label: string; description: string; color: string }> = {
  SOS:       { icon: ShieldAlert,   label: 'SOS móvil',         description: 'Botón SOS desde la app cliente',           color: 'bg-red-500/10 text-red-500' },
  PANIC:     { icon: AlertCircle,   label: 'Pánico',             description: 'Pánico desde panel o app móvil',           color: 'bg-red-600/10 text-red-600' },
  FIRE:      { icon: Flame,         label: 'Incendio',           description: 'Alarma de fuego/humo',                     color: 'bg-orange-500/10 text-orange-500' },
  MEDICAL:   { icon: Heart,         label: 'Médica',             description: 'Emergencia médica',                        color: 'bg-pink-500/10 text-pink-500' },
  INTRUSION: { icon: Shield,        label: 'Intrusión',          description: 'Detección de intrusión desde panel',       color: 'bg-purple-500/10 text-purple-500' },
  CALL:      { icon: Phone,         label: 'Llamada',            description: 'Solicitud de llamada del cliente',         color: 'bg-blue-500/10 text-blue-500' },
  DURESS:    { icon: ShieldAlert,   label: 'Coacción',           description: 'Código de coacción desde panel',           color: 'bg-rose-500/10 text-rose-500' },
  TAMPER:    { icon: AlertTriangle, label: 'Sabotaje',           description: 'Manipulación física del panel',            color: 'bg-amber-500/10 text-amber-500' },
};

// ─── Sound slot config ─────────────────────────────────────

type SlotKey = 'critical' | 'alarm' | 'warning' | 'processConfirm';

const SLOT_CONFIG: Record<SlotKey, { icon: React.ElementType; label: string; description: string; color: string }> = {
  critical:       { icon: ShieldAlert,   label: 'Eventos Críticos',       description: 'Robos, pánico, fuego — severidad CRÍTICO',                       color: 'bg-red-500/10 text-red-500' },
  alarm:          { icon: AlertTriangle, label: 'Alarmas',                 description: 'Zonas activadas, tamper — severidad ALARMA',                     color: 'bg-orange-500/10 text-orange-500' },
  warning:        { icon: Bell,          label: 'Advertencias',            description: 'Falla de energía, batería baja — severidad ADVERTENCIA',         color: 'bg-yellow-500/10 text-yellow-500' },
  processConfirm: { icon: CheckCircle2,  label: 'Confirmación al procesar', description: 'Tono al cerrar/procesar un evento (solo para el operador)',     color: 'bg-green-500/10 text-green-500' },
};

// ─── Sound Slot Card ───────────────────────────────────────

function SoundSlotCard({
  slotKey,
  slot,
  volume,
  masterDisabled,
}: {
  slotKey: SlotKey;
  slot: SoundSlot;
  volume: number;
  masterDisabled: boolean;
}) {
  const { setSlot, setCustomAudio } = useSettingsStore();
  const [expanded, setExpanded] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cfg = SLOT_CONFIG[slotKey];
  const Icon = cfg.icon;
  const hasCustom = !!slot.custom;

  function handlePreview() {
    if (previewing) return;
    setPreviewing(true);
    if (hasCustom && slot.custom) {
      playCustomAudio(slot.custom.dataUrl, volume);
    } else {
      playBuiltinSound(slot.builtin, volume);
    }
    setTimeout(() => setPreviewing(false), 1800);
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo no puede superar 2 MB. Graba un clip corto y vuelve a intentarlo.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCustomAudio(slotKey, { name: file.name, dataUrl: reader.result as string });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  return (
    <div className={cn('border rounded-lg overflow-hidden transition-opacity', masterDisabled && 'opacity-50 pointer-events-none')}>
      {/* ── Summary row ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={cn('p-2 rounded-lg flex-shrink-0', cfg.color)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{cfg.label}</div>
          <div className="text-xs text-muted-foreground">{cfg.description}</div>
          {hasCustom && (
            <div className="text-xs text-amber-500 mt-0.5 truncate">🎵 {slot.custom!.name}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handlePreview}
            disabled={!slot.enabled || previewing}
            title="Probar sonido"
            className="p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-40"
          >
            <Play className={cn('h-3.5 w-3.5', previewing ? 'text-amber-500 animate-pulse' : 'text-muted-foreground')} />
          </button>
          <Toggle checked={slot.enabled} onChange={(v) => setSlot(slotKey, { enabled: v })} />
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            title={expanded ? 'Colapsar' : 'Configurar sonido'}
          >
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
        </div>
      </div>

      {/* ── Expanded config ── */}
      {expanded && (
        <div className="border-t bg-muted/20 px-4 py-3 space-y-4">
          {/* Built-in preset */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
              Sonido incorporado
              {hasCustom && <span className="ml-2 font-normal text-amber-500">(anulado por sonido personalizado)</span>}
            </label>
            <div className="flex items-center gap-2">
              <select
                value={slot.builtin}
                onChange={(e) => setSlot(slotKey, { builtin: e.target.value as BuiltinSoundId })}
                disabled={hasCustom}
                className={cn(
                  'flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring',
                  hasCustom && 'opacity-40 cursor-not-allowed'
                )}
              >
                {(Object.entries(BUILTIN_SOUND_LABELS) as [BuiltinSoundId, string][])
                  .filter(([id]) => id !== 'voice_placeholder')
                  .map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
              </select>
              <button
                onClick={handlePreview}
                disabled={previewing}
                className="px-3 py-1.5 text-xs border rounded-md hover:bg-muted transition-colors disabled:opacity-40 flex items-center gap-1 whitespace-nowrap"
              >
                <Play className="h-3 w-3" />
                {previewing ? 'Reproduciendo...' : 'Probar'}
              </button>
            </div>
          </div>

          {/* Custom audio upload */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
              Sonido personalizado
              <span className="ml-1 font-normal">(MP3, WAV, OGG — máx. 2 MB)</span>
            </label>

            {hasCustom ? (
              <div className="flex items-center gap-2 p-2.5 rounded-lg border bg-amber-500/5 border-amber-500/20">
                <span className="text-lg leading-none">🎵</span>
                <span className="text-sm flex-1 truncate text-amber-600 dark:text-amber-400 font-medium">
                  {slot.custom!.name}
                </span>
                <button
                  onClick={handlePreview}
                  disabled={previewing}
                  className="p-1.5 rounded hover:bg-amber-500/20 transition-colors disabled:opacity-40"
                  title="Reproducir"
                >
                  <Play className="h-3.5 w-3.5 text-amber-500" />
                </button>
                <button
                  onClick={() => setCustomAudio(slotKey, undefined)}
                  className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
                  title="Eliminar sonido personalizado"
                >
                  <X className="h-3.5 w-3.5 text-red-500" />
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-muted-foreground/30 hover:border-muted-foreground/60 hover:bg-muted/50 transition-colors text-sm text-muted-foreground w-full justify-center"
                >
                  <Upload className="h-4 w-4" />
                  Subir archivo de audio
                </button>
                <p className="text-xs text-muted-foreground">
                  Graba una voz que diga "alerta", "alarma", "emergencia", etc. y súbela aquí. Al subir, reemplazará el sonido incorporado.
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Per-Alert-Type Sound Card ─────────────────────────────

function AlertTypeSoundCard({
  type,
  override,
  fallback,
  volume,
  masterDisabled,
}: {
  type: AlertType;
  /** The override slot the operator has saved for this type, if any. */
  override: SoundSlot | undefined;
  /** The slot used when no override exists (the "Eventos críticos" slot). */
  fallback: SoundSlot;
  volume: number;
  masterDisabled: boolean;
}) {
  const { setTypeSlot, setTypeCustomAudio, clearTypeSlot } = useSettingsStore();
  const [previewing, setPreviewing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cfg = ALERT_TYPE_CONFIG[type];
  const Icon = cfg.icon;
  const enabled = !!override;
  const slot: SoundSlot = override ?? fallback;
  const hasCustom = !!slot.custom;

  function handlePreview() {
    if (previewing) return;
    setPreviewing(true);
    if (hasCustom && slot.custom) {
      playCustomAudio(slot.custom.dataUrl, volume);
    } else {
      playBuiltinSound(slot.builtin, volume);
    }
    setTimeout(() => setPreviewing(false), 1800);
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo no puede superar 2 MB. Graba un clip corto y vuelve a intentarlo.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setTypeCustomAudio(type, { name: file.name, dataUrl: reader.result as string });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  return (
    <div className={cn('border rounded-lg overflow-hidden transition-opacity', masterDisabled && 'opacity-50 pointer-events-none')}>
      {/* ── Summary row ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={cn('p-2 rounded-lg flex-shrink-0', cfg.color)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{cfg.label}</span>
            {!enabled && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                usa crítico
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{cfg.description}</div>
          {enabled && hasCustom && (
            <div className="text-xs text-amber-500 mt-0.5 truncate">🎵 {slot.custom!.name}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handlePreview}
            disabled={previewing}
            title="Probar sonido"
            className="p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-40"
          >
            <Play className={cn('h-3.5 w-3.5', previewing ? 'text-amber-500 animate-pulse' : 'text-muted-foreground')} />
          </button>
          <Toggle
            checked={enabled}
            onChange={(v) => {
              if (v) {
                // Enable override seeded with the current critical slot.
                setTypeSlot(type, { enabled: true, builtin: fallback.builtin });
              } else {
                clearTypeSlot(type);
              }
            }}
          />
        </div>
      </div>

      {/* ── Expanded config (only when override is enabled) ── */}
      {enabled && (
        <div className="border-t bg-muted/20 px-4 py-3 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Sonido específico para alertas <strong>{cfg.label}</strong>.
            </span>
            <button
              onClick={() => clearTypeSlot(type)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              title="Volver al sonido crítico predeterminado"
            >
              <RotateCcw className="h-3 w-3" />
              Restablecer
            </button>
          </div>

          {/* Built-in preset */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
              Sonido incorporado
              {hasCustom && <span className="ml-2 font-normal text-amber-500">(anulado por sonido personalizado)</span>}
            </label>
            <div className="flex items-center gap-2">
              <select
                value={slot.builtin}
                onChange={(e) => setTypeSlot(type, { builtin: e.target.value as BuiltinSoundId })}
                disabled={hasCustom}
                className={cn(
                  'flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring',
                  hasCustom && 'opacity-40 cursor-not-allowed'
                )}
              >
                {(Object.entries(BUILTIN_SOUND_LABELS) as [BuiltinSoundId, string][])
                  .filter(([id]) => id !== 'voice_placeholder')
                  .map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
              </select>
              <button
                onClick={handlePreview}
                disabled={previewing}
                className="px-3 py-1.5 text-xs border rounded-md hover:bg-muted transition-colors disabled:opacity-40 flex items-center gap-1 whitespace-nowrap"
              >
                <Play className="h-3 w-3" />
                {previewing ? 'Reproduciendo...' : 'Probar'}
              </button>
            </div>
          </div>

          {/* Custom audio upload */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
              Sonido personalizado
              <span className="ml-1 font-normal">(MP3, WAV, OGG — máx. 2 MB)</span>
            </label>

            {hasCustom ? (
              <div className="flex items-center gap-2 p-2.5 rounded-lg border bg-amber-500/5 border-amber-500/20">
                <span className="text-lg leading-none">🎵</span>
                <span className="text-sm flex-1 truncate text-amber-600 dark:text-amber-400 font-medium">
                  {slot.custom!.name}
                </span>
                <button
                  onClick={handlePreview}
                  disabled={previewing}
                  className="p-1.5 rounded hover:bg-amber-500/20 transition-colors disabled:opacity-40"
                  title="Reproducir"
                >
                  <Play className="h-3.5 w-3.5 text-amber-500" />
                </button>
                <button
                  onClick={() => setTypeCustomAudio(type, undefined)}
                  className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
                  title="Eliminar sonido personalizado"
                >
                  <X className="h-3.5 w-3.5 text-red-500" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-muted-foreground/30 hover:border-muted-foreground/60 hover:bg-muted/50 transition-colors text-sm text-muted-foreground w-full justify-center"
              >
                <Upload className="h-4 w-4" />
                Subir archivo de audio
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Changelog inline components ─────────────────────────────

function VersionBadge({ version }: { version: string }) {
  const isUnreleased = version === 'Unreleased';
  return (
    <Badge variant={isUnreleased ? 'outline' : 'default'} className={isUnreleased ? 'border-dashed' : ''}>
      <Tag className="w-3 h-3 mr-1" />
      {isUnreleased ? version : `v${version}`}
    </Badge>
  );
}

function ChangelogEntryCard({ entry, isExpanded, onToggle }: { entry: ChangelogEntry; isExpanded?: boolean; onToggle?: () => void }) {
  const [localExpanded, setLocalExpanded] = useState(entry.version === 'Unreleased' || entry.version === '0.6.0');
  const expanded = isExpanded !== undefined ? isExpanded : localExpanded;
  const handleToggle = onToggle || (() => setLocalExpanded((v) => !v));
  return (
    <div className="border rounded-lg mb-3 overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        onClick={handleToggle}
      >
        <VersionBadge version={entry.version} />
        {entry.date && (
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {entry.date}
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t bg-muted/20">
          {entry.sections.map((section, idx) => {
            const cfg = SECTION_CONFIG[section.type];
            const Icon = cfg.icon;
            return (
              <div key={idx} className="pt-3">
                <Badge variant="outline" className={cn('mb-2', cfg.color)}>
                  <Icon className="w-3 h-3 mr-1" />
                  {cfg.label}
                </Badge>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground pl-2">
                  {section.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab type ─────────────────────────────────────────────────

type Tab = 'sonidos' | 'respuestas' | 'ntp' | 'smtp' | 'tracking' | 'audio' | 'soporte' | 'operaciones' | 'changelog';

// ─── SMTP Tab ─────────────────────────────────────────────────

interface SmtpDraft {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  from_name: string;
  from_email: string;
}

const EMPTY_SMTP: SmtpDraft = {
  host: '',
  port: 587,
  secure: false,
  username: '',
  password: '',
  from_name: 'ASI Security',
  from_email: '',
};

function SmtpTab() {
  const [draft, setDraft] = useState<SmtpDraft>(EMPTY_SMTP);
  const [passwordSet, setPasswordSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/smtp-settings`,
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
          }
        );
        const json = await res.json();
        if (json.config) {
          setDraft({ ...EMPTY_SMTP, ...json.config, password: '' });
          setPasswordSet(!!json.config.password_set);
          setIsDirty(false);
        }
      } catch (err) {
        console.error('smtp-settings load error', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function update<K extends keyof SmtpDraft>(key: K, value: SmtpDraft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
    setSaveResult(null);
  }

  async function handleSave() {
    setSaving(true);
    setSaveResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const body = {
        action: 'save',
        config: { ...draft, password: draft.password || '***' },
      };
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/smtp-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(body),
        }
      );
      const json = await res.json();
      if (json.ok) {
        setSaveResult({ ok: true, msg: 'Configuración guardada' });
        setIsDirty(false);
        if (draft.password) setPasswordSet(true);
        setDraft((prev) => ({ ...prev, password: '' }));
      } else {
        setSaveResult({ ok: false, msg: json.error ?? 'Error al guardar' });
      }
    } catch {
      setSaveResult({ ok: false, msg: 'Error de red' });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!testEmail.includes('@')) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const body = {
        action: 'test',
        config: { ...draft, password: draft.password || '***' },
        to: testEmail,
      };
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/smtp-settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(body),
        }
      );
      const json = await res.json();
      setTestResult({
        ok: json.ok,
        msg: json.ok ? (json.message ?? 'Correo enviado exitosamente') : (json.error ?? 'Error desconocido'),
      });
    } catch {
      setTestResult({ ok: false, msg: 'Error de red' });
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Cargando configuración...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Server className="h-5 w-5 text-amber-500" />
            Servidor SMTP de salida
          </CardTitle>
          <CardDescription>
            Configuración del servidor de correo utilizado para enviar notificaciones, restablecimiento
            de contraseñas y alertas por email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Separator />

          {/* Host + Port + TLS */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="text-sm font-medium mb-1.5 block">Host SMTP</label>
              <input
                type="text"
                value={draft.host}
                onChange={(e) => update('host', e.target.value)}
                placeholder="smtp.ejemplo.com"
                spellCheck={false}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Puerto</label>
              <input
                type="number"
                min={1}
                max={65535}
                value={draft.port}
                onChange={(e) => update('port', parseInt(e.target.value) || 587)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* TLS mode */}
          <div>
            <label className="text-sm font-medium mb-2 block">Modo de cifrado</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="tls"
                  checked={!draft.secure}
                  onChange={() => update('secure', false)}
                  className="accent-amber-500"
                />
                STARTTLS (puerto 587)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="tls"
                  checked={draft.secure}
                  onChange={() => update('secure', true)}
                  className="accent-amber-500"
                />
                SSL/TLS (puerto 465)
              </label>
            </div>
          </div>

          <Separator />

          {/* Credentials */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Usuario</label>
              <input
                type="text"
                autoComplete="username"
                value={draft.username}
                onChange={(e) => update('username', e.target.value)}
                placeholder="usuario@ejemplo.com"
                spellCheck={false}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                Contraseña
                {passwordSet && !draft.password && (
                  <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">(guardada — deja en blanco para mantener)</span>
                )}
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={draft.password}
                onChange={(e) => update('password', e.target.value)}
                placeholder={passwordSet ? '••••••••' : 'Contraseña SMTP'}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>
          </div>

          <Separator />

          {/* From address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre del remitente</label>
              <input
                type="text"
                value={draft.from_name}
                onChange={(e) => update('from_name', e.target.value)}
                placeholder="ASI Security"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Correo del remitente</label>
              <input
                type="email"
                value={draft.from_email}
                onChange={(e) => update('from_email', e.target.value)}
                placeholder="noreply@corporacionasi.com"
                spellCheck={false}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>
          </div>

          <Separator />

          {/* Save actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-md font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Server className="h-3.5 w-3.5" />}
              {saving ? 'Guardando...' : 'Guardar configuración'}
            </button>
            {saveResult && (
              <span className={cn('flex items-center gap-1.5 text-xs font-medium', saveResult.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                {saveResult.ok ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                {saveResult.msg}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="h-5 w-5 text-amber-500" />
            Probar conexión SMTP
          </CardTitle>
          <CardDescription>
            Envía un correo de prueba para verificar que la configuración es correcta. Se usará la
            configuración que acabas de guardar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => { setTestEmail(e.target.value); setTestResult(null); }}
              placeholder="destinatario@ejemplo.com"
              spellCheck={false}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
            <button
              onClick={handleTest}
              disabled={testing || !testEmail.includes('@') || !draft.host}
              className="flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
              {testing ? 'Enviando...' : 'Enviar prueba'}
            </button>
          </div>
          {testResult && (
            <div className={cn('flex items-start gap-2 text-sm rounded-lg px-3 py-2.5', testResult.ok ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-700 dark:text-red-400')}>
              {testResult.ok ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
              <span>{testResult.msg}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          La contraseña se almacena cifrada en la base de datos y nunca se muestra en texto claro.
          Cambios en esta configuración afectan todos los correos enviados por la plataforma (restablecimiento
          de contraseñas, notificaciones de emergencia, etc.).
        </span>
      </div>
    </div>
  );
}

// ─── NTP Tab ──────────────────────────────────────────────────

function NtpTab() {
  const { ntp, setNtp } = useSettingsStore();
  const [draft, setDraft] = useState<NtpSettings>(ntp);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [, forceRender] = useState(0);

  useEffect(() => { setDraft(ntp); }, [ntp]);

  const isDirty =
    draft.enabled !== ntp.enabled ||
    draft.endpointUrl !== ntp.endpointUrl ||
    draft.syncIntervalMin !== ntp.syncIntervalMin;

  function handleSave() { setNtp(draft); setTestResult(null); }

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await syncNtpOnce(draft.endpointUrl);
      const s = getNtpStatus();
      setTestResult({
        ok: true,
        msg: `Sincronizado. Desviación: ${s.skewMs >= 0 ? '+' : ''}${s.skewMs.toFixed(0)} ms`,
      });
    } catch (err) {
      setTestResult({ ok: false, msg: err instanceof Error ? err.message : 'Error desconocido' });
    } finally {
      setTesting(false);
      forceRender(v => v + 1);
    }
  }, [draft.endpointUrl]);

  const statusDisplay = (() => {
    const s = getNtpStatus();
    if (s.syncStatus === 'ok' && s.lastSyncAt) {
      const ago = Math.round((Date.now() - s.lastSyncAt) / 1000);
      const sign = s.skewMs >= 0 ? '+' : '';
      return {
        icon: <CheckCircle className="h-4 w-4 text-green-500" />,
        text: `Sincronizado hace ${ago}s — desviación: ${sign}${s.skewMs.toFixed(0)} ms`,
        cls: 'text-green-600 dark:text-green-400',
      };
    }
    if (s.syncStatus === 'error') {
      return {
        icon: <AlertCircle className="h-4 w-4 text-red-500" />,
        text: `Error: ${s.lastError}`,
        cls: 'text-red-600 dark:text-red-400',
      };
    }
    if (s.syncStatus === 'syncing') {
      return {
        icon: <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />,
        text: 'Sincronizando…',
        cls: 'text-blue-600 dark:text-blue-400',
      };
    }
    return {
      icon: <Clock className="h-4 w-4 text-muted-foreground" />,
      text: 'Sin sincronizar — usando reloj del sistema',
      cls: 'text-muted-foreground',
    };
  })();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="h-5 w-5 text-amber-500" />
              Sincronización de tiempo (NTP)
            </CardTitle>
            <Toggle checked={draft.enabled} onChange={v => setDraft(p => ({ ...p, enabled: v }))} />
          </div>
          <CardDescription>
            Sincroniza el reloj del navegador con un servidor NTP vía HTTP para mayor precisión en
            detección de tormentas de eventos en tiempo real.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Separator />

          {/* Status */}
          <div className={cn('flex items-center gap-2 text-sm', statusDisplay.cls)}>
            {statusDisplay.icon}
            <span>{statusDisplay.text}</span>
          </div>

          <div className={cn('space-y-4 transition-opacity', !draft.enabled && 'opacity-50 pointer-events-none')}>
            {/* Endpoint URL */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Endpoint HTTP</label>
              <input
                type="url"
                value={draft.endpointUrl}
                onChange={e => setDraft(p => ({ ...p, endpointUrl: e.target.value }))}
                placeholder="https://worldtimeapi.org/api/timezone/America/Guatemala"
                spellCheck={false}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                Debe retornar JSON con uno de estos campos:{' '}
                <code className="font-mono bg-muted px-1 rounded">ts</code> (unix ms),{' '}
                <code className="font-mono bg-muted px-1 rounded">datetime</code> o{' '}
                <code className="font-mono bg-muted px-1 rounded">utc_datetime</code> (ISO-8601).
                Por defecto usa la función Edge propia del proyecto.
              </p>
            </div>

            {/* Sync interval */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Intervalo de re-sincronización</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={0}
                  max={1440}
                  step={5}
                  value={draft.syncIntervalMin}
                  onChange={e => setDraft(p => ({ ...p, syncIntervalMin: Math.max(0, parseInt(e.target.value) || 0) }))}
                  className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <span className="text-sm text-muted-foreground">minutos (0 = solo al iniciar)</span>
              </div>
            </div>

            {/* Test */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleTest}
                disabled={testing || !draft.endpointUrl}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', testing && 'animate-spin')} />
                Probar conexión
              </button>
              {testResult && (
                <span className={cn('flex items-center gap-1.5 text-xs font-medium', testResult.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                  {testResult.ok ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                  {testResult.msg}
                </span>
              )}
            </div>
          </div>

          <Separator />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!isDirty}
              className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-md font-medium transition-colors disabled:opacity-50"
            >
              Guardar configuración
            </button>
            {isDirty && (
              <button onClick={() => setDraft(ntp)} className="px-4 py-1.5 border rounded-md text-xs hover:bg-muted transition-colors">
                Descartar
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Los navegadores no pueden conectarse a servidores NTP UDP directamente. Esta función usa un
          endpoint HTTP que retorna la hora del servidor. La desviación calculada se aplica a la
          detección de tormentas de eventos en tiempo real. Los timestamps de eventos almacenados
          provienen del servidor y no requieren corrección.
        </span>
      </div>
    </div>
  );
}

// ─── Quick Replies Tab ─────────────────────────────────────────

const MODAL_LABELS: Record<QuickReplyModal, string> = {
  both:      'Ambos modales (ARC)',
  process:   'Solo procesar evento',
  batch:     'Solo procesamiento en lote',
  sos:       'Chat SOS',
  call:      'Chat llamada',
  emergency: 'Todos los chats',
  all:       'Todos los contextos',
};

const MODAL_BADGE_CLS: Record<QuickReplyModal, string> = {
  both:      'bg-purple-500/10 text-purple-600 dark:text-purple-300 border-purple-500/20',
  process:   'bg-blue-500/10 text-blue-600 dark:text-blue-300 border-blue-500/20',
  batch:     'bg-amber-500/10 text-amber-600 dark:text-amber-300 border-amber-500/20',
  sos:       'bg-red-500/10 text-red-600 dark:text-red-300 border-red-500/20',
  call:      'bg-green-500/10 text-green-600 dark:text-green-300 border-green-500/20',
  emergency: 'bg-orange-500/10 text-orange-600 dark:text-orange-300 border-orange-500/20',
  all:       'bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20',
};

interface QRFormData {
  label: string;
  text: string;
  modals: QuickReplyModal;
  color: QuickReplyColor;
}

const EMPTY_FORM: QRFormData = { label: '', text: '', modals: 'both', color: 'amber' };

const COLOR_OPTIONS = Object.keys(CHIP_COLORS) as QuickReplyColor[];

function QRForm({
  form,
  setForm,
  onSave,
  onCancel,
  formValid,
}: {
  form: QRFormData;
  setForm: (fn: (prev: QRFormData) => QRFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  formValid: boolean;
}) {
  return (
    <div className="border border-amber-500/20 rounded-lg p-4 space-y-3 bg-amber-500/5">
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1 block">
          Etiqueta del badge <span className="font-normal">(máx. 40 caracteres)</span>
        </label>
        <input
          autoFocus
          value={form.label}
          onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
          placeholder="Ej: Prueba del sistema"
          maxLength={40}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1 block">
          Texto a insertar en el campo de razón
        </label>
        <textarea
          rows={2}
          value={form.text}
          onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
          placeholder="Texto completo que se insertará al hacer clic en el badge"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1 block">
          Mostrar en
        </label>
        <select
          value={form.modals}
          onChange={(e) => setForm((prev) => ({ ...prev, modals: e.target.value as QuickReplyModal }))}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <optgroup label="Eventos ARC (Alarmas)">
            <option value="both">Ambos (individual + lote)</option>
            <option value="process">Solo evento individual</option>
            <option value="batch">Solo procesamiento en lote</option>
          </optgroup>
          <optgroup label="Emergencias de Clientes">
            <option value="emergency">Todos los chats de emergencia</option>
            <option value="sos">Solo chat SOS</option>
            <option value="call">Solo chat llamada</option>
          </optgroup>
          <optgroup label="Global">
            <option value="all">Todos los contextos</option>
          </optgroup>
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">
          Color del badge
        </label>
        <div className="flex flex-wrap items-center gap-2">
          {COLOR_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              onClick={() => setForm((prev) => ({ ...prev, color: c }))}
              className={cn(
                'w-6 h-6 rounded-full transition-all ring-offset-2 ring-offset-background',
                CHIP_SWATCH_CLS[c],
                form.color === c ? 'ring-2 ring-foreground/60 scale-110' : 'opacity-70 hover:opacity-100 hover:scale-105',
              )}
            />
          ))}
          <span className={cn('ml-1 px-2.5 py-0.5 text-xs rounded-full border transition-colors', chipCls(form.color))}>
            {form.label || 'Vista previa'}
          </span>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onSave}
          disabled={!formValid}
          className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-md font-medium transition-colors disabled:opacity-50"
        >
          Guardar
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-1.5 border rounded-md text-xs hover:bg-muted transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function QuickRepliesTab() {
  const { quickReplies, addQuickReply, updateQuickReply, deleteQuickReply } = useSettingsStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<QRFormData>(EMPTY_FORM);

  const sorted = [...quickReplies].sort((a, b) => a.order - b.order);
  const formValid = form.label.trim().length > 0 && form.text.trim().length > 0;

  function handleStartAdd() {
    setAdding(true);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function handleStartEdit(r: QuickReply) {
    setEditingId(r.id);
    setAdding(false);
    setForm({ label: r.label, text: r.text, modals: r.modals, color: r.color ?? 'amber' });
  }

  function handleCancelForm() {
    setAdding(false);
    setEditingId(null);
  }

  function handleSaveAdd() {
    if (!formValid) return;
    addQuickReply(form);
    setAdding(false);
    setForm(EMPTY_FORM);
  }

  function handleSaveEdit() {
    if (!editingId || !formValid) return;
    updateQuickReply(editingId, form);
    setEditingId(null);
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-amber-500" />
              Respuestas rápidas
            </CardTitle>
            {!adding && !editingId && (
              <button
                onClick={handleStartAdd}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 transition-colors border border-amber-500/20"
              >
                <Plus className="h-3.5 w-3.5" />
                Nueva respuesta
              </button>
            )}
          </div>
          <CardDescription>
            Badges de texto predefinido que aparecen en los modales de procesamiento.
            Al hacer clic en un badge, su texto reemplaza el contenido del campo de razón.
          </CardDescription>
        </CardHeader>
      </Card>

      {sorted.length === 0 && !adding && (
        <div className="text-center py-8 text-sm text-muted-foreground border border-dashed rounded-xl">
          No hay respuestas rápidas configuradas.
          <br />
          <button onClick={handleStartAdd} className="mt-2 text-amber-500 hover:underline">
            Agregar la primera
          </button>
        </div>
      )}

      <div className="space-y-2">
        {sorted.map((r) =>
          editingId === r.id ? (
            <QRForm key={r.id} form={form} setForm={setForm} onSave={handleSaveEdit} onCancel={handleCancelForm} formValid={formValid} />
          ) : (
            <div key={r.id} className="flex items-start gap-3 border rounded-lg px-4 py-3 hover:bg-muted/30 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn('text-xs px-2.5 py-0.5 rounded-full border', chipCls(r.color))}>{r.label}</span>
                  <span className={cn('text-xs px-2 py-0.5 rounded-full border', MODAL_BADGE_CLS[r.modals])}>
                    {MODAL_LABELS[r.modals]}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.text}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleStartEdit(r)}
                  className="p-1.5 rounded-md hover:bg-muted transition-colors"
                  title="Editar"
                >
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
                <button
                  onClick={() => deleteQuickReply(r.id)}
                  className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors"
                  title="Eliminar"
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </button>
              </div>
            </div>
          )
        )}
        {adding && (
          <QRForm form={form} setForm={setForm} onSave={handleSaveAdd} onCancel={handleCancelForm} formValid={formValid} />
        )}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Los badges aparecen debajo del campo de razón. Selecciona <strong>Ambos modales</strong> para
          que aparezcan tanto al procesar un evento individual como al procesar en lote.
        </span>
      </div>
    </div>
  );
}

// ─── Tracking Tab (per-role GPS settings) ────────────────────

interface TrackingRow {
  role: string;
  enabled: boolean;
  desired_accuracy: 'HIGH' | 'MEDIUM' | 'LOW' | 'NAVIGATION';
  distance_filter_m: number;
  min_interval_seconds: number;
  stale_threshold_seconds: number;
  heartbeat_interval_seconds: number;
  stop_on_terminate: boolean;
  start_on_boot: boolean;
  notification_title: string;
  notification_text: string;
  updated_at: string;
}

const TRACKING_ROLE_LABEL: Record<string, string> = {
  client: 'Cliente (ASI Security)',
  patrol: 'Patrullero (ASI Patrol)',
};

function TrackingTab() {
  const [rows, setRows] = useState<TrackingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('tracking_settings')
        .select('*')
        .order('role');
      if (!cancelled) {
        if (error) console.error('[tracking] fetch error', error);
        setRows((data ?? []) as TrackingRow[]);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (role: string, patch: Partial<TrackingRow>) => {
    setRows((prev) => prev.map((r) => (r.role === role ? { ...r, ...patch } : r)));
  };

  const save = async (role: string) => {
    const row = rows.find((r) => r.role === role);
    if (!row) return;
    setSavingRole(role);
    const { error } = await supabase
      .from('tracking_settings')
      .update({
        enabled: row.enabled,
        desired_accuracy: row.desired_accuracy,
        distance_filter_m: row.distance_filter_m,
        min_interval_seconds: row.min_interval_seconds,
        stale_threshold_seconds: row.stale_threshold_seconds,
        heartbeat_interval_seconds: row.heartbeat_interval_seconds,
        stop_on_terminate: row.stop_on_terminate,
        start_on_boot: row.start_on_boot,
        notification_title: row.notification_title,
        notification_text: row.notification_text,
      })
      .eq('role', role);
    setSavingRole(null);
    if (error) {
      console.error('[tracking] save', error);
      alert('No se pudo guardar: ' + error.message);
      return;
    }
    setSavedFlash(role);
    setTimeout(() => setSavedFlash(null), 1500);
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Cargando configuración…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Los cambios aplican la próxima vez que la app móvil arranque o el usuario inicie sesión.
          El umbral de inactividad determina cuándo el marker se vuelve gris en el mapa de Centro de Monitoreo.
        </span>
      </div>

      {rows.map((row) => (
        <Card key={row.role}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-500" />
                {TRACKING_ROLE_LABEL[row.role] ?? row.role}
              </CardTitle>
              <Toggle checked={row.enabled} onChange={(v) => update(row.role, { enabled: v })} />
            </div>
            <CardDescription>
              Configuración de geolocalización en background para usuarios con rol <strong>{row.role}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className={cn('space-y-4 pt-0', !row.enabled && 'opacity-50')}>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Precisión deseada</label>
                <select
                  value={row.desired_accuracy}
                  onChange={(e) => update(row.role, { desired_accuracy: e.target.value as TrackingRow['desired_accuracy'] })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={!row.enabled}
                >
                  <option value="NAVIGATION">Navegación (máx. precisión, más batería)</option>
                  <option value="HIGH">Alta</option>
                  <option value="MEDIUM">Media</option>
                  <option value="LOW">Baja (ahorro batería)</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">
                  Distancia mínima entre fijos: <span className="font-mono">{row.distance_filter_m} m</span>
                </label>
                <input
                  type="range" min={0} max={200} step={5}
                  value={row.distance_filter_m}
                  onChange={(e) => update(row.role, { distance_filter_m: Number(e.target.value) })}
                  className="w-full accent-blue-500"
                  disabled={!row.enabled}
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">
                  Intervalo mínimo: <span className="font-mono">{row.min_interval_seconds}s</span>
                </label>
                <input
                  type="range" min={5} max={300} step={5}
                  value={row.min_interval_seconds}
                  onChange={(e) => update(row.role, { min_interval_seconds: Number(e.target.value) })}
                  className="w-full accent-blue-500"
                  disabled={!row.enabled}
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">
                  Umbral de inactividad (gris en mapa): <span className="font-mono">{Math.round(row.stale_threshold_seconds / 60)} min</span>
                </label>
                <input
                  type="range" min={60} max={1800} step={30}
                  value={row.stale_threshold_seconds}
                  onChange={(e) => update(row.role, { stale_threshold_seconds: Number(e.target.value) })}
                  className="w-full accent-blue-500"
                  disabled={!row.enabled}
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">
                  Heartbeat en segundo plano: <span className="font-mono">{Math.round((row.heartbeat_interval_seconds ?? 600) / 60)} min</span>
                </label>
                <input
                  type="range" min={60} max={1800} step={60}
                  value={row.heartbeat_interval_seconds ?? 600}
                  onChange={(e) => update(row.role, { heartbeat_interval_seconds: Number(e.target.value) })}
                  className="w-full accent-orange-500"
                  disabled={!row.enabled}
                />
                <p className="text-xs text-muted-foreground mt-1">Cada cuánto despierta el GPS cuando el usuario está quieto. Más alto = menos batería.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Título de la notificación</label>
                <input
                  type="text"
                  value={row.notification_title}
                  onChange={(e) => update(row.role, { notification_title: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={!row.enabled}
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Texto de la notificación</label>
                <input
                  type="text"
                  value={row.notification_text}
                  onChange={(e) => update(row.role, { notification_text: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={!row.enabled}
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-muted-foreground">
                Actualizado: {new Date(row.updated_at).toLocaleString('es-GT')}
              </span>
              <div className="flex items-center gap-2">
                {savedFlash === row.role && (
                  <span className="text-xs text-green-500 flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5" /> Guardado
                  </span>
                )}
                <button
                  onClick={() => save(row.role)}
                  disabled={savingRole === row.role}
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {savingRole === row.role ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Talkback Audio Tab ───────────────────────────────────────

interface TalkbackAudioConfig {
  gainDb: number;
  highpassHz: number;
  agc: boolean;
  limiterCeiling: number;
}

const DEFAULT_TALKBACK_AUDIO: TalkbackAudioConfig = {
  gainDb: 12,
  highpassHz: 200,
  agc: true,
  limiterCeiling: 0.97,
};

function TalkbackAudioTab() {
  const [cfg, setCfg] = useState<TalkbackAudioConfig>(DEFAULT_TALKBACK_AUDIO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: fetchErr } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'talkback_audio')
        .maybeSingle();
      if (cancelled) return;
      if (fetchErr) {
        console.error('[talkback-audio] load', fetchErr);
        setError(fetchErr.message);
      } else if (data?.value) {
        setCfg({ ...DEFAULT_TALKBACK_AUDIO, ...(data.value as Partial<TalkbackAudioConfig>) });
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const update = <K extends keyof TalkbackAudioConfig>(key: K, value: TalkbackAudioConfig[K]) => {
    setCfg((prev) => ({ ...prev, [key]: value }));
    setSavedFlash(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const { error: upsertErr } = await supabase
      .from('system_settings')
      .upsert({ key: 'talkback_audio', value: cfg }, { onConflict: 'key' });
    setSaving(false);
    if (upsertErr) {
      console.error('[talkback-audio] save', upsertErr);
      setError(upsertErr.message);
      return;
    }
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const reset = () => setCfg(DEFAULT_TALKBACK_AUDIO);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Cargando configuración…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Ajusta cómo procesa el gateway el audio del micrófono móvil antes de enviarlo a la
          bocina de la cámara. Los cambios aplican en ~30 s sin reiniciar nada.
        </span>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mic className="h-5 w-5 text-amber-500" />
            Audio de talkback (móvil → cámara)
          </CardTitle>
          <CardDescription className="text-xs">
            Procesamiento global aplicado a todas las cámaras al usar el botón de hablar (PTT).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="text-sm font-medium block mb-1">
              Ganancia de salida: <span className="font-mono">{cfg.gainDb > 0 ? '+' : ''}{cfg.gainDb} dB</span>
            </label>
            <input
              type="range" min={-6} max={24} step={1}
              value={cfg.gainDb}
              onChange={(e) => update('gainDb', Number(e.target.value))}
              className="w-full accent-amber-500"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>−6 (silencioso)</span>
              <span>+12 (recomendado)</span>
              <span>+24 (máximo)</span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">
              Filtro de graves (highpass): <span className="font-mono">{cfg.highpassHz === 0 ? 'desactivado' : `${cfg.highpassHz} Hz`}</span>
            </label>
            <input
              type="range" min={0} max={500} step={20}
              value={cfg.highpassHz}
              onChange={(e) => update('highpassHz', Number(e.target.value))}
              className="w-full accent-amber-500"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Quita ruido grave (ventiladores, manipulación del teléfono). 200 Hz es típico para voz.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Compresor / AGC</div>
              <div className="text-[11px] text-muted-foreground">
                Sube el volumen de la voz cuando hablas bajito sin distorsionar cuando hablas fuerte.
              </div>
            </div>
            <Toggle checked={cfg.agc} onChange={(v) => update('agc', v)} />
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">
              Limitador (techo): <span className="font-mono">{Math.round(cfg.limiterCeiling * 100)}%</span>
            </label>
            <input
              type="range" min={0.5} max={1} step={0.01}
              value={cfg.limiterCeiling}
              onChange={(e) => update('limiterCeiling', Number(e.target.value))}
              className="w-full accent-amber-500"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Evita el clipping (distorsión cuadrada). 97% es seguro, no bajar de 80%.
            </p>
          </div>

          {error && (
            <div className="text-xs text-red-500 bg-red-500/10 rounded px-2 py-1.5">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
            <Button variant="ghost" onClick={reset} disabled={saving}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restaurar valores por defecto
            </Button>
            {savedFlash && (
              <span className="text-xs text-green-500 flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5" /> Guardado
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Operaciones Tab ─────────────────────────────────────────

// Seconds a patrol has to accept an assigned alert before the server moves it to the next
// patrol (or back to PENDING). Read by patrol_accept_timeout() in the DB, clamped there to 30–900.
const ACCEPT_TIMEOUT_OPTIONS = [
  { value: 60, label: '1 minuto' },
  { value: 90, label: '1 minuto 30 segundos' },
  { value: 120, label: '2 minutos' },
  { value: 180, label: '3 minutos' },
  { value: 300, label: '5 minutos' },
  { value: 600, label: '10 minutos' },
];

function OperacionesTab() {
  const [autoAssign, setAutoAssign] = useState(false);
  const [sosMinimap, setSosMinimap] = useState(true);
  const [acceptTimeout, setAcceptTimeout] = useState(120);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['auto_assign_patrol', 'sos_minimap_enabled', 'patrol_accept_timeout_seconds']);
      if (!cancelled) {
        const map = Object.fromEntries((data ?? []).map((r: { key: string; value: unknown }) => [r.key, r.value]));
        setAutoAssign(map.auto_assign_patrol === true || map.auto_assign_patrol === 'true');
        const timeout = Number(map.patrol_accept_timeout_seconds);
        if (Number.isFinite(timeout) && timeout > 0) setAcceptTimeout(timeout);
        // Default true if key doesn't exist yet
        setSosMinimap(map.sos_minimap_enabled !== false && map.sos_minimap_enabled !== 'false');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const saveSetting = async <T extends boolean | number>(key: string, value: T, setter: (v: T) => void) => {
    setter(value);
    setSaving(key);
    const { error } = await supabase
      .from('system_settings')
      .upsert({ key, value }, { onConflict: 'key' });
    setSaving(null);
    if (!error) {
      setSavedFlash(key);
      setTimeout(() => setSavedFlash(null), 1500);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <RefreshCw className="h-4 w-4 animate-spin" /> Cargando…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Navigation2 className="h-5 w-5 text-blue-500" />
              Asignación automática de patrullero
            </CardTitle>
            <div className="flex items-center gap-2">
              {saving === 'auto_assign_patrol' && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              {savedFlash === 'auto_assign_patrol' && (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Guardado
                </span>
              )}
              <Toggle checked={autoAssign} onChange={(v) => saveSetting('auto_assign_patrol', v, setAutoAssign)} />
            </div>
          </div>
          <CardDescription>
            Cuando está activado, el sistema asigna automáticamente el patrullero más cercano
            al recibir una alerta SOS o Pánico. Si está desactivado, la asignación es manual
            desde el Centro de Recepción de Alarmas.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Tiempo para aceptar una alerta
            </CardTitle>
            <div className="flex items-center gap-2">
              {saving === 'patrol_accept_timeout_seconds' && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              {savedFlash === 'patrol_accept_timeout_seconds' && (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Guardado
                </span>
              )}
              <select
                value={acceptTimeout}
                onChange={(e) => saveSetting('patrol_accept_timeout_seconds', Number(e.target.value), setAcceptTimeout)}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {!ACCEPT_TIMEOUT_OPTIONS.some((o) => o.value === acceptTimeout) && (
                  <option value={acceptTimeout}>{acceptTimeout} segundos</option>
                )}
                {ACCEPT_TIMEOUT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <CardDescription>
            Tiempo que tiene el patrullero para aceptar una alerta asignada en la app ASI Patrol.
            Si no la acepta, el sistema la pasa a la siguiente patrulla disponible; si no hay otra,
            la alerta vuelve a pendiente para que el operador la asigne.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-500" />
              Mini-mapa en ticket SOS
            </CardTitle>
            <div className="flex items-center gap-2">
              {saving === 'sos_minimap_enabled' && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              {savedFlash === 'sos_minimap_enabled' && (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Guardado
                </span>
              )}
              <Toggle checked={sosMinimap} onChange={(v) => saveSetting('sos_minimap_enabled', v, setSosMinimap)} />
            </div>
          </div>
          <CardDescription>
            Muestra el mapa con la ubicación del cliente y el patrullero en la pantalla de seguimiento
            de alerta de la app móvil. Desactívalo temporalmente si el número de unidades de patrulla
            no permite dar una cobertura adecuada.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Estas configuraciones son globales y aplican a todas las cuentas activas.
          Los cambios tienen efecto inmediato.
        </span>
      </div>
    </div>
  );
}

// ─── Support Tab (WhatsApp config) ───────────────────────────

function SupportTab() {
  const [whatsapp, setWhatsapp] = useState('');
  const [message, setMessage] = useState('');
  const [cooldownDays, setCooldownDays] = useState('3');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['support_whatsapp', 'support_whatsapp_message', 'phone_reminder_cooldown_days']);
      if (cancelled) return;
      if (!error && data) {
        const map = new Map<string, unknown>(data.map((r: { key: string; value: unknown }) => [r.key, r.value]));
        setWhatsapp(String(map.get('support_whatsapp') ?? ''));
        setMessage(String(map.get('support_whatsapp_message') ?? ''));
        setCooldownDays(String(map.get('phone_reminder_cooldown_days') ?? '3'));
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function handleSave() {
    setSaving(true);
    setResult(null);
    try {
      const rows = [
        { key: 'support_whatsapp',                value: whatsapp.trim() },
        { key: 'support_whatsapp_message',        value: message.trim() },
        { key: 'phone_reminder_cooldown_days',    value: cooldownDays.trim() },
      ];
      const { error } = await supabase.from('system_settings').upsert(rows, { onConflict: 'key' });
      if (error) {
        setResult({ ok: false, msg: error.message });
      } else {
        setResult({ ok: true, msg: 'Configuración guardada' });
        setDirty(false);
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Cargando configuración…
      </div>
    );
  }

  const trimmed = whatsapp.trim();
  const previewHref = trimmed
    ? `https://wa.me/${trimmed.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`
    : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-amber-500" />
            Contacto de soporte por WhatsApp
          </CardTitle>
          <CardDescription>
            Número y mensaje predeterminado que aparece en la app móvil cuando el usuario
            necesita ayuda (botón "Contactar soporte" en el banner de perfil).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Número WhatsApp (con código país, sin espacios)</label>
            <input
              type="text"
              value={whatsapp}
              onChange={(e) => { setWhatsapp(e.target.value); setDirty(true); setResult(null); }}
              placeholder="+50255749328"
              spellCheck={false}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Ejemplo Guatemala: <span className="font-mono">+50255749328</span>
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">Mensaje predeterminado</label>
            <textarea
              value={message}
              onChange={(e) => { setMessage(e.target.value); setDirty(true); setResult(null); }}
              placeholder="Hola, necesito ayuda con ASI Security."
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
          </div>

          {previewHref && (
            <a
              href={previewHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:underline"
            >
              <MessageSquare className="h-3.5 w-3.5" /> Probar el enlace
            </a>
          )}

          <Separator />

          <div>
            <label className="text-sm font-medium mb-1.5 block">Días de silencio del recordatorio de teléfono</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={cooldownDays}
                onChange={(e) => { setCooldownDays(e.target.value); setDirty(true); setResult(null); }}
                className="w-24 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <span className="text-sm text-muted-foreground">días</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Cuando el usuario toca «Más tarde» en el banner de perfil, el banner no vuelve a aparecer durante estos días.
            </p>
          </div>

          <Separator />

          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
            {result && (
              <span className={cn('flex items-center gap-1.5 text-xs font-medium', result.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                {result.ok ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                {result.msg}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Estos valores se leen desde la app móvil al abrir el banner de soporte. Los cambios
          aplican de inmediato; la app vuelve a consultar este valor cada vez que se abre la
          pantalla de inicio.
        </span>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function SettingsPage() {
  const { sounds, setSounds } = useSettingsStore();
  const [activeTab, setActiveTab] = useState<Tab>('sonidos');
  const masterDisabled = !sounds.masterEnabled;
  const currentVersion = CHANGELOG_DATA.find((e) => e.version !== 'Unreleased')?.version ?? '0.0.0';

  // Changelog pagination
  const [changelogPage, setChangelogPage] = useState(1);
  const [changelogStartDate, setChangelogStartDate] = useState('');
  const [changelogEndDate, setChangelogEndDate] = useState('');
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set([CHANGELOG_DATA[0]?.version]));
  const changelogItemsPerPage = 5;

  const filteredChangelog = CHANGELOG_DATA.filter((entry) => {
    if (!changelogStartDate && !changelogEndDate) return true;
    if (!entry.date) return true;
    const entryDate = new Date(entry.date);
    if (changelogStartDate && entryDate < new Date(changelogStartDate)) return false;
    if (changelogEndDate && entryDate > new Date(changelogEndDate)) return false;
    return true;
  });

  const changelogTotalPages = Math.ceil(filteredChangelog.length / changelogItemsPerPage);
  const changelogStartIdx = (changelogPage - 1) * changelogItemsPerPage;
  const changelogEndIdx = changelogStartIdx + changelogItemsPerPage;
  const paginatedChangelog = filteredChangelog.slice(changelogStartIdx, changelogEndIdx);

  const toggleVersion = (version: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(version)) next.delete(version);
      else next.add(version);
      return next;
    });
  };

  useEffect(() => {
    setChangelogPage(1);
  }, [changelogStartDate, changelogEndDate]);

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="w-6 h-6" />
            Configuración del Sistema
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Sonidos de notificación, historial de cambios y preferencias del sistema
          </p>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-xs text-muted-foreground">Versión</div>
          <div className="text-xl font-mono font-bold text-muted-foreground">v{currentVersion}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-muted p-1 rounded-lg w-fit">
        {([
          ['sonidos',    Volume2,       'Sonidos'],
          ['respuestas', MessageSquare, 'Respuestas rápidas'],
          ['ntp',        Radio,         'Sincronización NTP'],
          ['smtp',       Mail,          'Servidor SMTP'],
          ['tracking',   MapPin,        'Tracking GPS'],
          ['audio',      Mic,           'Audio talkback'],
          ['operaciones', Navigation2,  'Operaciones'],
          ['soporte',    HelpCircle,    'Soporte (WhatsApp)'],
          ['changelog',  FileText,      'Historial de cambios'],
        ] as const).map(([id, Icon, label]) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── TAB: SONIDOS ──────────────────────────────────── */}
      {activeTab === 'sonidos' && (
        <div className="space-y-4">
          {/* Master toggle + volume in one card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  {sounds.masterEnabled ? <Volume2 className="h-5 w-5 text-amber-500" /> : <VolumeX className="h-5 w-5 text-muted-foreground" />}
                  Sonidos del sistema
                </CardTitle>
                <Toggle checked={sounds.masterEnabled} onChange={(v) => setSounds({ masterEnabled: v })} />
              </div>
              <CardDescription>
                Interruptor maestro para todas las notificaciones de audio
              </CardDescription>
            </CardHeader>
            <CardContent className={cn('pt-0 space-y-3', masterDisabled && 'opacity-50 pointer-events-none')}>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Volumen global</span>
                  <span className="text-sm font-mono text-muted-foreground">{Math.round(sounds.volume * 100)}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <VolumeX className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <input
                    type="range" min={0} max={1} step={0.05}
                    value={sounds.volume}
                    onChange={(e) => setSounds({ volume: Number(e.target.value) })}
                    className="flex-1 h-2 accent-amber-500 cursor-pointer"
                  />
                  <Volume2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Per-slot sound cards */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2 px-1">Configuración por severidad</h3>
            <div className="space-y-2">
              {(Object.keys(SLOT_CONFIG) as SlotKey[]).map((key) => (
                <SoundSlotCard
                  key={key}
                  slotKey={key}
                  slot={sounds[key]}
                  volume={sounds.volume}
                  masterDisabled={masterDisabled}
                />
              ))}
            </div>
          </div>

          {/* Per-alert-type overrides */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2 px-1">Sonido por tipo de alerta</h3>
            <p className="text-xs text-muted-foreground mb-2 px-1">
              Activa un tipo para asignarle un sonido propio. Los tipos sin activar usan el sonido de
              <strong> Eventos críticos</strong>.
            </p>
            <div className="space-y-2">
              {(Object.keys(ALERT_TYPE_CONFIG) as AlertType[]).map((type) => (
                <AlertTypeSoundCard
                  key={type}
                  type={type}
                  override={sounds.byType[type]}
                  fallback={sounds.critical}
                  volume={sounds.volume}
                  masterDisabled={masterDisabled}
                />
              ))}
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Haz clic en <Play className="inline h-3 w-3" /> junto a cada tipo para probar el sonido.
              Los sonidos solo funcionan después de una interacción del usuario con la página
              (por política del navegador). Usa el triángulo ▶ para desplegar la configuración
              de cada tipo y elegir entre sonidos incorporados o subir tu propio archivo de audio.
            </span>
          </div>
        </div>
      )}

      {/* ── TAB: RESPUESTAS RÁPIDAS ───────────────────────── */}
      {activeTab === 'respuestas' && <QuickRepliesTab />}

      {/* ── TAB: NTP ──────────────────────────────────────── */}
      {activeTab === 'ntp' && <NtpTab />}

      {/* ── TAB: SMTP ─────────────────────────────────────── */}
      {activeTab === 'smtp' && <SmtpTab />}

      {/* ── TAB: TRACKING GPS ─────────────────────────────── */}
      {activeTab === 'tracking' && <TrackingTab />}

      {activeTab === 'audio' && <TalkbackAudioTab />}

      {/* ── TAB: SOPORTE ──────────────────────────────────── */}
      {activeTab === 'operaciones' && <OperacionesTab />}
      {activeTab === 'soporte' && <SupportTab />}

      {/* ── TAB: CHANGELOG ────────────────────────────────── */}
      {activeTab === 'changelog' && (
        <div className="flex flex-col h-[calc(100vh-280px)]">
          <p className="text-sm text-muted-foreground mb-4">
            Registro de versiones y cambios del sistema ASI Security Platform
          </p>

          {/* Filtros de fecha */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Desde</label>
              <Input
                type="date"
                value={changelogStartDate}
                onChange={(e) => setChangelogStartDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Hasta</label>
              <Input
                type="date"
                value={changelogEndDate}
                onChange={(e) => setChangelogEndDate(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          {/* Paginación superior */}
          {changelogTotalPages > 1 && (
            <div className="flex items-center justify-between pb-3 border-b mb-4">
              <div className="text-sm text-muted-foreground">
                Página {changelogPage} de {changelogTotalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setChangelogPage((p) => Math.max(1, p - 1))}
                  disabled={changelogPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setChangelogPage((p) => Math.min(changelogTotalPages, p + 1))}
                  disabled={changelogPage === changelogTotalPages}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Lista de changelog scrollable */}
          <div className="flex-1 overflow-y-auto border rounded-lg p-4 bg-muted/20">
            <div className="space-y-3">
              {paginatedChangelog.length > 0 ? (
                paginatedChangelog.map((entry, idx) => (
                  <ChangelogEntryCard
                    key={idx}
                    entry={entry}
                    isExpanded={expandedVersions.has(entry.version)}
                    onToggle={() => toggleVersion(entry.version)}
                  />
                ))
              ) : (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No se encontraron versiones en el rango de fechas seleccionado.
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Paginación inferior */}
          {changelogTotalPages > 1 && (
            <div className="flex items-center justify-between pt-3 border-t mt-4">
              <div className="text-sm text-muted-foreground">
                Página {changelogPage} de {changelogTotalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setChangelogPage((p) => Math.max(1, p - 1))}
                  disabled={changelogPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setChangelogPage((p) => Math.min(changelogTotalPages, p + 1))}
                  disabled={changelogPage === changelogTotalPages}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
